import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Instant } from "@js-joda/core";
import { IQueryOptions } from "api/api-hooks/api-hooks-common";
import { useGetDatasetMapperData } from "api/api-hooks/dataset-mapper-hooks";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import useTimerThreshold from "hooks/useTimerThreshold";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sparklines, SparklinesBars, SparklinesLine } from "react-sparklines";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { toInstant } from "utils/time-utils";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import { formatNumber } from "../charts/utils";
import SparkLineChart from "../sparkline-advanced/SparkLineChart";
import "./SparkLine.scss";
import SparkLineDisconnectedLabel from "./SparkLineDisconnectedLabel";
import SparkLineLabel from "./SparkLineLabel";
import { ISparkLineHandlerProps, LineConfig, ValueLabel } from "./SparkLineTypes";

const SparkLineHandler = (props: ISparkLineHandlerProps) => {
  const selectedCharts = useDashboardStore(state => state.selectedChartsIdentifiers);
  const setSelectedCharts = useDashboardStore(state => state.setSelectedChartsIdentifiers);

  const [currentDialogDatasets, setCurrentDialogDatasets] = useState<string[]>([]);

  const decimalPlaces = props.decimalPlaces || 0;
  const { data: mapperData } = useGetDatasetMapperData(props.stringValueMapper ?? 0, {
    enabled: !!props.stringValueMapper,
  });

  const showExpandedChart =
    !props.isMultipleCharts ||
    (props.isMultipleCharts && (props.advancedProps?.isExpanded || props.generalProperties?.expandAll));

  const value = props.dataArray[props.dataArray.length - 1];

  const { isExpired, resetTimer } = useTimerThreshold(props.maxAge, props.lastValueTime?.toEpochMilli());

  useEffect(() => {
    resetTimer(props.lastValueTime?.toEpochMilli());
  }, [props.lastValueTime, resetTimer]);

  const startDate = useMemo(
    () => toInstant(props.advancedProps?.fromDate ?? Instant.now()),
    [props.advancedProps?.fromDate]
  );

  const endDate = useMemo(
    () => toInstant(props.advancedProps?.toDate ?? Instant.now()),
    [props.advancedProps?.toDate]
  );

  const getValues = useCallback(
    (config?: LineConfig, data?: InstantDoubleValue[]) => {
      if (config?.usage === "constant") {
        return [
          { x: startDate.toEpochMilli(), y: config.constant ?? 0 },
          { x: endDate.toEpochMilli(), y: config.constant ?? 0 },
        ];
      }
      if (config?.usage === "dataset" && data) {
        return data.map(item => ({
          x: Instant.parse(item.key).toEpochMilli(),
          y: item.value ?? 0,
        }));
      }
      return [];
    },
    [startDate, endDate]
  );

  const hasDuplicatedDatasets = useMemo(() => {
    const datasetIds = [
      props.dataset,
      props.advancedProps?.baseline?.dataset,
      props.advancedProps?.minLine?.dataset,
      props.advancedProps?.maxLine?.dataset,
    ].filter(Boolean);

    const uniqueSet = new Set(datasetIds);
    return uniqueSet.size !== datasetIds.length;
  }, [props.advancedProps]);

  const queryParams = useCallback(
    (
      identifier?: string,
      isDatasetUsage?: boolean
    ): [
      string[],
      {
        timeStart: string;
        timeEnd: string;
        downsampleInterval?: number;
        useDefaultDownsampleInterval?: boolean;
      },
      IQueryOptions,
    ] => [
      [identifier!],
      {
        timeStart: startDate?.toString(),
        timeEnd: endDate?.toString(),
        downsampleInterval: props.advancedProps?.downsampleInterval,
        useDefaultDownsampleInterval: true,
      },
      {
        refetchInterval: 0,
        enabled: !!identifier && isDatasetUsage && props.dataArray.length > 0 && !hasDuplicatedDatasets,
        keepPreviousData: true,
      },
    ],
    [endDate, startDate]
  );

  const { data: baselineDatasetData } = useGetDataInfoRange(
    ...queryParams(props.advancedProps?.baseline?.dataset, props.advancedProps?.baseline?.usage === "dataset")
  );
  const { data: minLineDatasetData } = useGetDataInfoRange(
    ...queryParams(props.advancedProps?.minLine?.dataset, props.advancedProps?.minLine?.usage === "dataset")
  );
  const { data: maxLineDatasetData } = useGetDataInfoRange(
    ...queryParams(props.advancedProps?.maxLine?.dataset, props.advancedProps?.maxLine?.usage === "dataset")
  );

  const baselineValues = useMemo(
    () => getValues(props.advancedProps?.baseline, baselineDatasetData?.[0]?.data),
    [props.advancedProps?.baseline, baselineDatasetData, getValues]
  );

  const minValues = useMemo(
    () => getValues(props.advancedProps?.minLine, minLineDatasetData?.[0]?.data),
    [props.advancedProps?.minLine, minLineDatasetData, getValues]
  );

  const maxValues = useMemo(
    () => getValues(props.advancedProps?.maxLine, maxLineDatasetData?.[0]?.data),
    [props.advancedProps?.maxLine, maxLineDatasetData, getValues]
  );

  const getDecimalPlaces = () => {
    if (!props.advancedProps?.useDecimalsFromHundred) {
      if (value >= 100) {
        return 0;
      }
    }
    return decimalPlaces;
  };

  const valueContent = useMemo(() => {
    if (isExpired) {
      return (
        <SparkLineDisconnectedLabel isMultipleCharts={props.isMultipleCharts} isLoading={props.isLoading} />
      );
    }
    if (value != null) {
      return `${formatNumber(value, getDecimalPlaces())} ${props.unit ?? ""}`;
    }
    return (
      <SparkLineDisconnectedLabel isMultipleCharts={props.isMultipleCharts} isLoading={props.isLoading} />
    );
  }, [isExpired, value, props.isLoading]);

  const valueTextColor = useMemo(() => {
    if (isExpired) return "red";
    if (value == null) return "red";
    return "inherit";
  }, [isExpired, value, props.advancedProps?.maxLine, props.advancedProps?.minLine, maxValues, minValues]);

  const [dialog, setDialog] = useState<boolean>(false);
  const [valueLabel, setValueLabel] = useState<ValueLabel>({
    disconnected: false,
    value,
    decimalPlaces,
    title: props.chartTitle,
    content: valueContent,
    bgColor: "rgba(255, 255, 255, 0.6)",
    textColor: valueTextColor,
  });

  useEffect(() => {
    setValueLabel(prev => ({
      ...prev,
      value,
      decimalPlaces,
      content: valueContent,
      textColor: valueTextColor,
    }));
  }, [value, decimalPlaces, props.unit, valueContent, valueTextColor]);

  let el;
  if ((props.dataArray && props.dataArray.length !== 0) || props.advancedProps?.baseline?.dataset) {
    el = (
      <div className="flex w-full h-full items-center justify-content-center">
        {props.advanced ? (
          <SparkLineChart
            dataset={props.dataset}
            advancedProps={props.advancedProps!}
            lineColor={props.lineColor}
            lineWidth={props.lineWidth}
            chartWidth={props.sparklineWidth}
            chartHeight={props.height}
            minYAxis={props.minYAxis}
            maxYAxis={props.maxYAxis}
            isInlineMultipleCharts={props.advancedProps?.isMultipleChartsInOneBox}
            minValues={minValues}
            maxValues={maxValues}
            baselineValues={baselineValues}
          />
        ) : (
          <div
            style={{
              lineHeight: "2rem",
              width: props.sparklineWidth ? `${props.sparklineWidth}px` : "100%",
              height: props.height ? `${props.height}px` : "100%",
              display: "inline-block",
            }}
          >
            <Sparklines data={props.dataArray} height={30} preserveAspectRatio="none">
              <SparklinesLine
                style={{
                  stroke: props.lineColor ?? "#41c3f9",
                  fill: "none",
                  strokeWidth: props.lineWidth ?? 1,
                }}
              />
              <SparklinesBars style={{ fill: props.lineColor ?? "#41c3f9", fillOpacity: ".25" }} />
            </Sparklines>
          </div>
        )}
      </div>
    );
  } else {
    el = props.isLoading ? null : <h4>{T("word.no-data")}</h4>;
  }

  const identifiers = [
    props.dataset,
    props.advancedProps?.baseline?.usage === "dataset" && props.advancedProps?.baseline?.dataset,
    props.advancedProps?.maxLine?.usage === "dataset" && props.advancedProps?.maxLine?.dataset,
    props.advancedProps?.minLine?.usage === "dataset" && props.advancedProps?.minLine?.dataset,
  ].filter(Boolean) as string[];

  const getClassName = () => {
    if (props.isMultipleCharts) {
      return `${props.advancedProps?.isExpanded || props.generalProperties?.expandAll ? "sparkline-expanded" : "sparkline-collapsed cursor-pointer"} flex flex-column-reverse ${props.advancedProps?.isMultipleChartsInOneBox ? "h-full" : ""}`;
    }
    return `flex flex-column h-full`;
  };

  const getHeight = () => {
    if (!props.isMultipleCharts) {
      return `${props.height ? `${props.height}px` : "100%"}`;
    }

    if (props.advancedProps?.isMultipleChartsInOneBox) {
      return `${props.height ? `${props.height}px` : "161px"}`;
    }

    return "100%";
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.ctrlKey) {
      if (selectedCharts.length > 0) {
        setCurrentDialogDatasets(selectedCharts);
      } else {
        setCurrentDialogDatasets(identifiers);
      }
    } else {
      setCurrentDialogDatasets(identifiers);
    }
    setDialog(true);
  };

  const chartElement = props.isLoading ? (
    <div className="inline-multiple-charts-spinner">
      <ProgressSpinner className="h-2rem w-2rem" />
    </div>
  ) : (
    <div
      className={
        props.advancedProps?.isMultipleChartsInOneBox
          ? "inline-multiple-charts-sparkline"
          : "flex flex-wrap justify-content-evenly w-full relative"
      }
      style={!props.advancedProps?.isMultipleChartsInOneBox ? { height: getHeight() } : undefined}
      onDoubleClick={handleDoubleClick}
    >
      {el}
    </div>
  );

  const sparkLineLabel = (
    <SparkLineLabel
      valueLabel={valueLabel}
      setValueLabel={setValueLabel}
      isMultipleCharts={props.isMultipleCharts}
      generalProperties={props.generalProperties}
      isExpanded={props.advancedProps?.isExpanded ?? false}
      {...props}
      mappedValue={
        props.stringValueMapper && mapperData ? mapperData.mappings[valueLabel.value as number] : undefined
      }
      advancedProps={props.advancedProps}
    />
  );

  const changeExpandedChartIndex = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.ctrlKey) {
      const newSelectedCharts = [...selectedCharts];
      if (newSelectedCharts.includes(props.dataset)) {
        newSelectedCharts.splice(newSelectedCharts.indexOf(props.dataset), 1);
      } else {
        newSelectedCharts.push(props.dataset);
      }
      setSelectedCharts(newSelectedCharts);
    }
    return (
      !props.advancedProps?.isMultipleChartsInOneBox &&
      props.advancedProps?.setExpandedChartIndex?.(props.chartIndex ?? 0)
    );
  };

  const contentToRender = (
    <>
      <div
        className={getClassName()}
        onClick={changeExpandedChartIndex}
        style={{
          borderRadius: "5px",
          border: selectedCharts.includes(props.dataset) ? "1px solid #007df2" : "",
        }}
      >
        <div
          className="sparkline-chart-container"
          onClick={() => setSelectedCharts([])}
          onDoubleClick={handleDoubleClick}
        >
          {props.advancedProps?.isMultipleChartsInOneBox || showExpandedChart ? chartElement : null}
        </div>
        <div className="sparkline-label-container">{sparkLineLabel}</div>
      </div>
      <Dialog
        header={T("label.sparkline-datasets-chart")}
        visible={dialog}
        className="sparkline-datasets-chart"
        modal={false}
        onHide={() => setDialog(false)}
      >
        <DatasetLineChart
          title=""
          identifiers={currentDialogDatasets}
          from={props.advancedProps?.fromDate?.toString()}
          to={props.advancedProps?.toDate?.toString()}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </>
  );

  return contentToRender;
};

export default SparkLineHandler;
